import React from 'react';
import PropTypes from 'prop-types';
import Button from '@material-ui/core/Button';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Dialog from '@material-ui/core/Dialog';
import RadioGroup from '@material-ui/core/RadioGroup';
import Radio from '@material-ui/core/Radio';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import InputBase from '@material-ui/core/InputBase';
import SearchRoundedIcon from '@material-ui/icons/SearchRounded';
import {fade, makeStyles} from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import JString from '@easytech-international-sdn-bhd/jstring';

const useStyles = makeStyles(theme => ({
	paper: {
		borderRadius: 12,
		overflow: 'hidden',
	},
	dialogTitle: {
		padding: theme.spacing(3, 3, 2, 3),
	},
	titlePrimary: {
		fontSize: '1.25rem',
		fontWeight: 600,
		lineHeight: 1.3,
		color: theme.palette.text.primary,
	},
	searchRow: {
		marginTop: theme.spacing(2),
	},
	search: {
		position: 'relative',
		borderRadius: 12,
		backgroundColor: fade(theme.palette.text.primary, 0.04),
		border: `1px solid ${fade(theme.palette.text.primary, 0.08)}`,
		transition: theme.transitions.create(
			['background-color', 'box-shadow', 'border-color'],
			{duration: 200}
		),
		'&:hover': {
			borderColor: fade(theme.palette.text.primary, 0.16),
		},
		'&:focus-within': {
			borderColor: theme.palette.primary.main,
			boxShadow: `0 0 0 3px ${fade(theme.palette.primary.main, 0.16)}`,
		},
	},
	searchIcon: {
		padding: theme.spacing(0, 1.5),
		height: '100%',
		position: 'absolute',
		pointerEvents: 'none',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		color: theme.palette.text.secondary,
	},
	inputRoot: {
		color: theme.palette.text.primary,
		width: '100%',
	},
	inputInput: {
		padding: theme.spacing(1.25, 1, 1.25, 0),
		paddingLeft: `calc(1em + ${theme.spacing(4)}px)`,
		fontSize: '0.9375rem',
		width: '100%',
	},
	dialogContent: {
		padding: 0,
		borderTop: `1px solid ${theme.palette.divider}`,
	},
	scrollContainer: {
		maxHeight: 360,
		overflowY: 'auto',
		padding: theme.spacing(1),
		'&::-webkit-scrollbar': {width: 8},
		'&::-webkit-scrollbar-track': {backgroundColor: 'transparent'},
		'&::-webkit-scrollbar-thumb': {
			backgroundColor: fade(theme.palette.text.primary, 0.2),
			borderRadius: 4,
			'&:hover': {
				backgroundColor: fade(theme.palette.text.primary, 0.32),
			},
		},
		scrollbarWidth: 'thin',
		scrollbarColor: `${fade(theme.palette.text.primary, 0.2)} transparent`,
	},
	radioGroup: {
		width: '100%',
	},
	listRow: {
		width: '100%',
		margin: 0,
		paddingLeft: theme.spacing(1),
		paddingRight: theme.spacing(2),
		borderRadius: 8,
		transition: theme.transitions.create('background-color', {duration: 150}),
		'&:hover': {
			backgroundColor: theme.palette.action.hover,
		},
	},
	listRowSelected: {
		backgroundColor: fade(theme.palette.primary.main, 0.08),
		'&:hover': {
			backgroundColor: fade(theme.palette.primary.main, 0.12),
		},
	},
	listLabel: {
		fontSize: '0.9375rem',
		fontWeight: 500,
		color: theme.palette.text.primary,
	},
	listLabelSelected: {
		fontWeight: 600,
	},
	emptyState: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		padding: theme.spacing(6, 2),
		gap: theme.spacing(1),
		minHeight: 240,
	},
	emptyIcon: {
		fontSize: 56,
		color: theme.palette.text.disabled,
		opacity: 0.4,
		marginBottom: theme.spacing(1),
	},
	emptyTitle: {
		fontSize: '1rem',
		fontWeight: 600,
		color: theme.palette.text.secondary,
	},
	emptySubtitle: {
		fontSize: '0.8125rem',
		color: theme.palette.text.disabled,
	},
	actions: {
		padding: theme.spacing(2, 3),
		gap: theme.spacing(1),
		borderTop: `1px solid ${theme.palette.divider}`,
	},
	resetBtn: {
		color: theme.palette.error.main,
		fontWeight: 600,
		textTransform: 'none',
		'&:hover': {
			backgroundColor: fade(theme.palette.error.main, 0.08),
		},
	},
	spacer: {flex: 1},
	cancelBtn: {
		textTransform: 'none',
		fontWeight: 500,
		borderRadius: 8,
	},
	doneBtn: {
		textTransform: 'none',
		fontWeight: 600,
		borderRadius: 8,
		boxShadow: 'none',
		'&:hover': {
			boxShadow: `0 4px 12px ${fade(theme.palette.primary.main, 0.32)}`,
		},
	},
}));

function escapeRegex(string) {
	return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export default function ListView(props) {

	const classes = useStyles();
	const {onClose, onSelect, value: valueProp, open, title, language, options, ...other} = props;
	const [value, setValue] = React.useState(valueProp);
	const [searchText, setSearchText] = React.useState('');
	const radioGroupRef = React.useRef(null);

	React.useEffect(() => {
		if (!open) {
			setValue(valueProp);
			setSearchText('');
		}
	}, [valueProp, open]);

	const handleEntering = () => {
		if (radioGroupRef.current != null) {
			radioGroupRef.current.focus();
		}
	};

	const handleCancel = () => {
		onClose();
	};

	const handleOk = () => {
		onSelect(value);
	};

	const handleChange = (event) => {
		if (event.target) {
			setValue(value => (event.target.value));
		}
		event.persist();
	};

	const onChangeSearchText = (event) => {
		setSearchText(event.target.value);
	};

	const realOptions = options.slice(0, options.length - 1);
	const text = JString(escapeRegex(String(searchText)));
	const search = (text.str).split(' ');
	const filteredOptions = text.isValid()
		? realOptions.filter(option => {
			const searchString = String(option.value) + String(option.name);
			return search.every(term => JString(searchString).searchDeep(term));
		})
		: realOptions;

	return (
		<Dialog
			disableBackdropClick
			disableEscapeKeyDown
			maxWidth="xs"
			fullWidth
			onEntering={handleEntering}
			aria-labelledby="confirmation-dialog-title"
			open={open}
			PaperProps={{className: classes.paper}}
			{...other}>
			<DialogTitle id="confirmation-dialog-title" className={classes.dialogTitle} disableTypography>
				<Typography component="div" className={classes.titlePrimary}>
					{title || ''}
				</Typography>
				<div className={classes.searchRow}>
					<div className={classes.search}>
						<div className={classes.searchIcon}>
							<SearchRoundedIcon/>
						</div>
						<InputBase
							value={searchText}
							placeholder={language.search}
							classes={{root: classes.inputRoot, input: classes.inputInput}}
							inputProps={{'aria-label': 'search'}}
							onChange={onChangeSearchText}/>
					</div>
				</div>
			</DialogTitle>
			<DialogContent className={classes.dialogContent}>
				{filteredOptions.length === 0 ? (
					<div className={classes.emptyState}>
						<SearchRoundedIcon className={classes.emptyIcon}/>
						<Typography className={classes.emptyTitle}>
							{language.no_matches_found}
						</Typography>
						<Typography className={classes.emptySubtitle}>
							{language.try_different_keyword}
						</Typography>
					</div>
				) : (
					<div className={classes.scrollContainer}>
						<RadioGroup
							ref={radioGroupRef}
							className={classes.radioGroup}
							value={value}
							onChange={handleChange}>
							{filteredOptions.map((option, idx) => {
								const isSelected = String(option.value) === String(value);
								return (
									<FormControlLabel
										value={option.value}
										key={String(idx)}
										control={<Radio color="primary"/>}
										label={option.name}
										className={`${classes.listRow} ${isSelected ? classes.listRowSelected : ''}`}
										classes={{label: `${classes.listLabel} ${isSelected ? classes.listLabelSelected : ''}`}}/>
								);
							})}
						</RadioGroup>
					</div>
				)}
			</DialogContent>
			<DialogActions className={classes.actions}>
				<Button
					className={classes.resetBtn}
					size="small"
					onClick={() => onSelect(options[options.length - 1]?.value)}>
					{language.reset_selection}
				</Button>
				<div className={classes.spacer}/>
				<Button
					variant="outlined"
					className={classes.cancelBtn}
					onClick={handleCancel}>
					{language.cancel}
				</Button>
				<Button
					variant="contained"
					color="primary"
					className={classes.doneBtn}
					onClick={handleOk}>
					{language.ok}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

ListView.propTypes = {
	onClose: PropTypes.func.isRequired,
	open: PropTypes.bool.isRequired,
	value: PropTypes.string.isRequired,
	options: PropTypes.array.isRequired,
	language: PropTypes.object.isRequired,
	title: PropTypes.string.isRequired
};
